// ExtendedDB に対する派生計算ヘルパー。
// 帳票・QuantityPlanPage 等が共通利用する。

import type { ExtendedDB } from "./extendedDB";
import { deriveAreaProcessCompletions } from "./derivedSelectors";

export type WorkSubtypeProgress = {
  workSubtypeId: number;
  workSubtypeName: string;
  unit: string;
  plannedQty: number;
  actualQty: number;
  areaCount: number;
  completedAreaCount: number;
};

// 種別単位の進捗を計算する。
// 計画: 該当施工箇所の planned_qty 合算。
// 実績: primary_work_detail_id がある場合は主工程完了で全量計上、
//       無い場合は工程完了レイヤー数比率で按分。
// 完了状況は ProcessExecution.completed===true 行を派生関数で AreaProcessCompletion 形式へ変換して利用。
export function computeWorkSubtypeProgress(
  workSubtypeId: number,
  db: Pick<ExtendedDB, "workSubtypes" | "workDetails" | "constructionPlanAreas" | "processExecutions">,
  workFilter?: number,
): WorkSubtypeProgress {
  const subtype = db.workSubtypes.find((s) => s.id === workSubtypeId && s.deleted_at == null);
  const wdsOfSubtype = db.workDetails.filter((d) => d.deleted_at == null && d.work_subtype_id === workSubtypeId);
  const areas = db.constructionPlanAreas.filter((a) =>
    a.deleted_at == null && a.work_subtype_id === workSubtypeId && (workFilter == null || a.work_id === workFilter)
  );

  const completions = deriveAreaProcessCompletions(db);

  let plannedSum = 0;
  let actualSum = 0;
  let completedAreaCount = 0;

  const primaryWdId = subtype?.primary_work_detail_id ?? null;

  for (const area of areas) {
    const planned = area.planned_qty ?? 0;
    plannedSum += planned;

    const allCompleted = wdsOfSubtype.length > 0 && wdsOfSubtype.every((wd) =>
      completions.some((c) => c.area_id === area.id && c.work_detail_id === wd.id),
    );
    if (allCompleted) completedAreaCount += 1;

    if (primaryWdId != null) {
      const primaryDone = completions.some((c) =>
        c.area_id === area.id && c.work_detail_id === primaryWdId,
      );
      if (primaryDone) actualSum += planned;
    } else if (wdsOfSubtype.length > 0) {
      const total = wdsOfSubtype.length;
      const done = wdsOfSubtype.filter((wd) =>
        completions.some((c) => c.area_id === area.id && c.work_detail_id === wd.id),
      ).length;
      actualSum += planned * (done / total);
    }
  }

  return {
    workSubtypeId,
    workSubtypeName: subtype?.name ?? "?",
    unit: subtype?.unit ?? "",
    plannedQty: Math.round(plannedSum * 100) / 100,
    actualQty: Math.round(actualSum * 100) / 100,
    areaCount: areas.length,
    completedAreaCount,
  };
}

// Lot の計画 QR 総数。Lot.planned_count を真のソースとする。
export function getLotPlannedTotal(lot: { planned_count: number }): number {
  return lot.planned_count;
}
